/**
 * E1b — Zod schemas for all contract types.
 * Used at API boundaries to validate request bodies and response shapes.
 */
import { z } from 'zod';
// ── Enums ─────────────────────────────────────────────────────────────────────
export const runStatusSchema = z.enum(['queued', 'planning', 'running', 'paused', 'succeeded', 'failed', 'cancelled']);
export const layerStatusSchema = z.enum(['pending', 'running', 'succeeded', 'failed', 'skipped']);
export const entityStatusSchema = z.enum(['pending', 'extracting', 'deploying', 'verifying', 'succeeded', 'failed', 'skipped']);
export const runModeSchema = z.enum(['validate', 'deploy']);
export const checkTypeSchema = z.enum(['count', 'lookup', 'custom']);
export const severitySchema = z.enum(['info', 'warn', 'error']);
export const deployStateSchema = z.enum(['idle', 'running', 'succeeded', 'failed', 'cancelled']);
export const engineCapabilitySchema = z.enum([
    'env-status', 'adhoc', 'validate-only', 'cancel',
    'per-entity-retry', 'rules-preview', 'partial-layers', 'live-logs',
    'approvals', 'rollback',
]);
export const gateStatusSchema = z.enum(['pending', 'approved']);
export const deploymentGateSchema = z.object({
    id: z.string().min(1),
    edgeId: z.string().optional(),
    layerId: z.string().optional(),
    requiredApprovals: z.number().int().nonnegative(),
    approvals: z.array(z.object({ by: z.string().min(1) })),
    status: gateStatusSchema,
});
// ── Core shapes ───────────────────────────────────────────────────────────────
export const deploymentEntitySchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    layer: z.string().min(1),
    tool: z.string().min(1),
    status: entityStatusSchema,
    apiName: z.string().optional(),
    recordCount: z.number().int().nonnegative().optional(),
    processedCount: z.number().int().nonnegative().optional(),
    errorCount: z.number().int().nonnegative().optional(),
    progress: z.number().min(0).max(100).optional(),
    errors: z.array(z.string()).optional(),
    // The entity-level failure reason (one message about the step). See DeploymentEntity.error.
    error: z.string().optional(),
    failedRecords: z.array(z.object({
        object: z.string().min(1),
        recordKey: z.string().min(1),
        name: z.string().optional(),
        errorCode: z.string().optional(),
        message: z.string(),
        phase: z.enum(['upsert', 'activation', 'fk-resolution']),
    })).optional(),
    startedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().optional(),
    durationMs: z.number().nonnegative().optional(),
});
export const deploymentLayerSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    status: layerStatusSchema,
    entities: z.array(deploymentEntitySchema),
    recordCount: z.number().int().nonnegative().optional(),
    errorCount: z.number().int().nonnegative().optional(),
    startedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().optional(),
    durationMs: z.number().nonnegative().optional(),
});
export const runVerificationResultSchema = z.object({
    entityId: z.string().min(1),
    check: z.string().min(1),
    type: checkTypeSchema,
    status: z.enum(['pass', 'fail', 'skip']),
    expected: z.number().optional(),
    actual: z.number().optional(),
    notes: z.string().optional(),
});
export const deploymentRuleSchema = z.object({
    id: z.string().min(1),
    entityId: z.string().min(1),
    title: z.string().min(1),
    description: z.string().min(1),
    severity: severitySchema,
});
export const runTotalsSchema = z.object({
    layers: z.number().int().nonnegative(),
    entities: z.number().int().nonnegative(),
    records: z.number().int().nonnegative(),
    errors: z.number().int().nonnegative(),
});
export const deploymentRunSchema = z.object({
    id: z.string().min(1),
    status: runStatusSchema,
    mode: runModeSchema,
    sourceOrg: z.string().min(1),
    targetOrg: z.string().min(1),
    layers: z.array(deploymentLayerSchema),
    verifications: z.array(runVerificationResultSchema),
    totals: runTotalsSchema,
    gates: z.array(deploymentGateSchema).optional(),
    pipelineId: z.string().optional(),
    startedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().optional(),
    durationMs: z.number().nonnegative().optional(),
    initiatedBy: z.string().optional(),
    error: z.string().optional(),
});
export const deploymentRunSummarySchema = z.object({
    id: z.string().min(1),
    status: runStatusSchema,
    mode: runModeSchema,
    sourceOrg: z.string().min(1),
    targetOrg: z.string().min(1),
    totals: runTotalsSchema,
    startedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().optional(),
});
export const engineMetaSchema = z.object({
    contractVersion: z.string().min(1),
    engineName: z.string().min(1),
    engineVersion: z.string().min(1),
    capabilities: z.array(engineCapabilitySchema),
});
export const deployRequestSchema = z.object({
    sourceOrg: z.string().min(1, 'sourceOrg is required'),
    targetOrg: z.string().min(1, 'targetOrg is required'),
    mode: runModeSchema,
    pipelineId: z.string().optional(),
    layerIds: z.array(z.string().min(1)).optional(),
    entityIds: z.array(z.string().min(1)).optional(),
    recordFilter: z.string().optional(),
}).refine((data) => data.sourceOrg !== data.targetOrg, { message: 'sourceOrg and targetOrg must be different', path: ['targetOrg'] });
export const environmentStatusSchema = z.object({
    orgAlias: z.string().min(1),
    state: deployStateSchema,
    deployedBranch: z.string().optional(),
    deployedSha: z.string().optional(),
    lastDeployedAt: z.string().datetime().optional(),
    deployedBy: z.string().optional(),
    drift: z.boolean().optional(),
    driftCount: z.number().int().nonnegative().optional(),
    pendingApprovals: z.number().int().nonnegative().optional(),
    activeRunId: z.string().optional(),
});
export const deploymentPlanSchema = z.object({
    sourceOrg: z.string().min(1),
    targetOrg: z.string().min(1),
    mode: runModeSchema,
    layers: z.array(deploymentLayerSchema),
    rules: z.array(deploymentRuleSchema),
    gates: z.array(deploymentGateSchema).optional(),
    estimatedDuration: z.number().nonnegative().optional(),
});
//# sourceMappingURL=engine.js.map